import { useState } from "react";
import { UploadCloud, CheckCircle2, AlertTriangle, FileSpreadsheet, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, money } from "../api";
import ProjectMasterImport from "../components/ProjectMasterImport";

export default function ExcelImport({ onDone }) {
  const [mode, setMode] = useState("finance"); // finance | master
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [strategy, setStrategy] = useState("skip");
  const [clearBefore, setClearBefore] = useState(true);
  const [tab, setTab] = useState("summary");

  const doPreview = async () => {
    if (!file) return toast.error("Önce bir Excel dosyası seçin");
    setLoading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/imports/excel/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      toast.success("Dosya analiz edildi");
    } catch (e) {
      toast.error("Analiz başarısız: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  const doCommit = async () => {
    if (!preview) return;
    if (clearBefore && !window.confirm("Mevcut tüm veriler silinip Excel'den yeniden yüklenecek. Onaylıyor musunuz?")) return;
    setLoading(true);
    try {
      const r = await api.post("/imports/excel/commit", {
        employers: preview.employers, subcontractors: preview.subcontractors,
        projects: preview.projects, work_items: preview.work_items, payments: preview.payments,
        conflict_strategy: strategy, clear_before: clearBefore, filename: preview.filename,
      });
      toast.success(`İçe aktarma tamam! ${JSON.stringify(r.data.stats)}`);
      setPreview(null); setFile(null); onDone?.();
    } catch (e) {
      toast.error("İçe aktarma başarısız: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  return (
    <main className="content" data-testid="page-import">
      <div className="page-head">
        <div>
          <span className="section-kicker">VERİ AKTARIMI</span>
          <h1>Excel İçe Aktarma</h1>
          <p>Finans takip Excel'inizi (işveren, alt yüklenici, iş kalemi, ödeme) veya proje künye/master listenizi (tür, konum, lisans, kapasite) içe aktarın.</p>
        </div>
      </div>

      <div className="segmented" style={{ marginBottom: 20, maxWidth: 420 }}>
        <button className={mode === "finance" ? "selected" : ""} onClick={() => setMode("finance")} data-testid="mode-finance">Finans Verisi</button>
        <button className={mode === "master" ? "selected" : ""} onClick={() => setMode("master")} data-testid="mode-master">Proje Künyesi</button>
      </div>

      {mode === "master" && <ProjectMasterImport onDone={onDone} />}

      {mode === "finance" && (
      <>
      <section className="panel upload-panel" data-testid="upload-panel">
        <div className="upload-drop">
          <UploadCloud size={30} />
          <b>{file ? file.name : "Excel dosyası seçin (.xlsx)"}</b>
          <input type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files[0])} data-testid="file-input" />
        </div>
        <div className="upload-actions">
          <label className="check-label">
            <input type="checkbox" checked={clearBefore} onChange={(e) => setClearBefore(e.target.checked)} data-testid="clear-before-checkbox" />
            Mevcut verileri temizle (önerilir)
          </label>
          <label className="check-label">Çakışma:
            <select value={strategy} onChange={(e) => setStrategy(e.target.value)} data-testid="strategy-select">
              <option value="skip">Atla</option>
              <option value="update">Güncelle</option>
              <option value="create_new">Yeni oluştur</option>
            </select>
          </label>
          <button className="new-btn" disabled={!file || loading} onClick={doPreview} data-testid="preview-button">
            <FileSpreadsheet size={16} /> {loading ? "Analiz ediliyor..." : "Analiz et"}
          </button>
        </div>
      </section>

      {preview && (
        <>
          <section className="panel" data-testid="preview-summary">
            <div className="panel-head">
              <div><span className="eyebrow">ÖNİZLEME</span><h2>{preview.filename}</h2></div>
              <button className="new-btn" disabled={loading} onClick={doCommit} data-testid="commit-button">
                <CheckCircle2 size={16} /> İçe Aktar
              </button>
            </div>
            <div className="import-stats">
              <div><small>İŞVEREN</small><b>{preview.summary.employers}</b></div>
              <div><small>ALT YÜKLENİCİ</small><b>{preview.summary.subcontractors}</b></div>
              <div><small>PROJE</small><b>{preview.summary.projects}</b></div>
              <div><small>İŞ KALEMİ</small><b>{preview.summary.work_items}</b></div>
              <div><small>ÖDEME</small><b>{preview.summary.payments}</b></div>
            </div>

            <div className="segmented" style={{ marginTop: 20 }}>
              {["summary","projects","work_items","payments","employers","subcontractors"].map(t => (
                <button key={t} className={tab === t ? "selected" : ""} onClick={() => setTab(t)} data-testid={`tab-${t}`}>{t}</button>
              ))}
            </div>

            {tab === "summary" && (
              <div className="preview-list">
                <h3>Sayfalar:</h3>
                {preview.sheets.map(s => (
                  <div key={s.name} className="preview-row">
                    <b>{s.name}</b><span>{s.rows} satır · {s.cols} kolon</span>
                    {s.error && <em className="status status-amber"><AlertTriangle size={12} /> {s.error}</em>}
                  </div>
                ))}
              </div>
            )}
            {tab === "projects" && (
              <div className="project-table">
                <div className="table-row table-head prev-prj">
                  <span>#</span><span>PROJE</span><span>İŞVEREN</span><span>YATIRIMCI</span><span>KAPASİTE</span><span>BEDEL</span>
                </div>
                {preview.projects.slice(0, 100).map((p, i) => (
                  <div className="table-row prev-prj" key={i}>
                    <span>{p.project_no || "-"}</span>
                    <b>{p.name}</b>
                    <span>{p.employer_short}</span>
                    <span>{p.investor_short || "—"}</span>
                    <span>{p.capacity || "—"}</span>
                    <b className="mono-small">{money(p.agreed_amount)}</b>
                  </div>
                ))}
              </div>
            )}
            {tab === "work_items" && (
              <div className="project-table">
                <div className="table-row table-head prev-wi">
                  <span>PROJE</span><span>KATEGORİ</span><span>KAPSAM</span><span>ALT YÜK.</span><span>BEDEL</span>
                </div>
                {preview.work_items.slice(0, 200).map((w, i) => (
                  <div className="table-row prev-wi" key={i}>
                    <b>{w.project_name}</b>
                    <span>{w.category}</span>
                    <em className={`status ${w.scope_status === "Yok" ? "status-blue" : "status-amber"}`}>{w.scope_status}</em>
                    <span>{w.subcontractor_short || "—"}</span>
                    <b className="mono-small">{money(w.agreed_amount)}</b>
                  </div>
                ))}
              </div>
            )}
            {tab === "payments" && (
              <div className="project-table">
                <div className="table-row table-head prev-pay">
                  <span>PROJE</span><span>ALT YÜK.</span><span>AŞAMA</span><span>TUTAR</span><span>TARİH</span><span>DURUM</span>
                </div>
                {preview.payments.slice(0, 200).map((p, i) => (
                  <div className="table-row prev-pay" key={i}>
                    <b>{p.project_name}</b>
                    <span>{p.subcontractor_short}</span>
                    <span>{p.stage}</span>
                    <b className="mono-small">{money(p.amount)}</b>
                    <span>{p.date ? String(p.date).slice(0, 10) : "—"}</span>
                    <em className={`status ${p.status === "Planlanan" ? "status-blue" : "status-green"}`}>{p.status}</em>
                  </div>
                ))}
              </div>
            )}
            {tab === "employers" && (
              <div className="project-table">
                <div className="table-row table-head prev-emp"><span>KISA AD</span><span>ŞİRKET</span><span>VERGİ</span></div>
                {preview.employers.map((e, i) => (
                  <div className="table-row prev-emp" key={i}>
                    <b>{e.short_name}</b><span>{e.name}</span><span>{e.tax_no || "—"}</span>
                  </div>
                ))}
              </div>
            )}
            {tab === "subcontractors" && (
              <div className="project-table">
                <div className="table-row table-head prev-emp"><span>KISA AD</span><span>ŞİRKET</span><span>VERGİ</span></div>
                {preview.subcontractors.map((s, i) => (
                  <div className="table-row prev-emp" key={i}>
                    <b>{s.short_name}</b><span>{s.name}</span><span>{s.tax_no || "—"}</span>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
      </>
      )}
    </main>
  );
}
